/**
 * Selling-plan totals for PDP pack rows — allocations + OTP + percentage fallback
 * (Subscribe card aligns with Shopify variant selling_plan_allocations).
 */

export function normalizeSellingPlanId (id) {
  if (id == null || id === '') return ''
  const s = String(id)
  const match = /SellingPlan\/(\d+)/i.exec(s)
  if (match) return match[1]
  const n = Number(s)
  return Number.isFinite(n) ? String(n) : s
}

function hasSellingPlanSelection (plan) {
  if (plan == null) return false
  return plan.id != null && plan.id !== ''
}

function normalizeSellingPlanName (name) {
  return String(name || '').trim().toLowerCase()
}

export function parseSellingPlanAllocationsJson (attr) {
  if (attr == null || attr === '') return []
  try {
    const v = JSON.parse(attr)
    return Array.isArray(v) ? v : []
  } catch {
    return []
  }
}

function applySellingPlanAdjustmentsFallback (otpBaseCents, selectedSellingPlan) {
  const adjustments = selectedSellingPlan?.price_adjustments
  if (!Array.isArray(adjustments) || adjustments.length === 0) return null

  const adj = adjustments[0]
  if (adj?.value_type !== 'percentage') return null

  const discount = (otpBaseCents / 100) * Number(adj.value)
  return Math.round(otpBaseCents - discount)
}

function computeSellingTotalCents ({
  otpBaseCents,
  allocationsArr,
  selectedSellingPlan,
  selectedSellingPlanIndex,
}) {
  if (!Number.isFinite(otpBaseCents)) return null

  if (!hasSellingPlanSelection(selectedSellingPlan)) {
    return otpBaseCents
  }

  const want = normalizeSellingPlanId(selectedSellingPlan.id)
  if (want && Array.isArray(allocationsArr) && allocationsArr.length > 0) {
    const match = allocationsArr.find(
      (a) => normalizeSellingPlanId(a?.selling_plan_id) === want
    )
    if (match != null && match.price != null) {
      const cents = Number(match.price)
      if (Number.isFinite(cents)) return cents
    }
  }

  const wantName = normalizeSellingPlanName(selectedSellingPlan.name)
  if (wantName && Array.isArray(allocationsArr) && allocationsArr.length > 0) {
    const match = allocationsArr.find(
      (a) => normalizeSellingPlanName(a?.selling_plan_name || a?.name) === wantName
    )
    if (match != null && match.price != null) {
      const cents = Number(match.price)
      if (Number.isFinite(cents)) return cents
    }
  }

  if (
    Number.isInteger(selectedSellingPlanIndex) &&
    selectedSellingPlanIndex >= 0 &&
    Array.isArray(allocationsArr) &&
    allocationsArr[selectedSellingPlanIndex]?.price != null
  ) {
    const cents = Number(allocationsArr[selectedSellingPlanIndex].price)
    if (Number.isFinite(cents)) return cents
  }

  const fromAdj = applySellingPlanAdjustmentsFallback(
    otpBaseCents,
    selectedSellingPlan
  )
  if (fromAdj != null && Number.isFinite(fromAdj)) return fromAdj

  return otpBaseCents
}

/**
 * Hydrated pack row anchors carry SSR data-* (survives sibling swap when Pinia
 * only holds the active child product — sibling variant may be absent from store).
 */
export function getVariantSellingTotalForPackRow ({
  otpCentsRaw,
  allocationsJson,
  variantFallback,
  selectedSellingPlan,
  selectedSellingPlanIndex,
}) {
  let otpBase = otpCentsRaw != null ? Number(String(otpCentsRaw).trim()) : NaN
  if (!Number.isFinite(otpBase) && variantFallback?.price != null) {
    otpBase = Number(variantFallback.price)
  }
  if (!Number.isFinite(otpBase)) return null

  let allocations = parseSellingPlanAllocationsJson(allocationsJson)
  if (
    allocations.length === 0 &&
    Array.isArray(variantFallback?.selling_plan_allocations) &&
    variantFallback.selling_plan_allocations.length > 0
  ) {
    allocations = variantFallback.selling_plan_allocations
  }

  return computeSellingTotalCents({
    otpBaseCents: otpBase,
    allocationsArr: allocations,
    selectedSellingPlan,
    selectedSellingPlanIndex,
  })
}

export function getVariantSellingTotalCents (variant, selectedSellingPlan) {
  if (variant == null || variant.price == null) return null

  return computeSellingTotalCents({
    otpBaseCents: Number(variant.price),
    allocationsArr: variant.selling_plan_allocations,
    selectedSellingPlan,
    selectedSellingPlanIndex: -1,
  })
}

export function formatPerCanLine (templateStr, formattedMoney) {
  if (typeof templateStr !== 'string' || !templateStr) {
    return formattedMoney
  }
  return templateStr.split('___PRICE___').join(formattedMoney)
}
